import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import ApiURL from '../Api/Api';

const WorkerNotifications = ({ onLatestRequestUpdate }) => {
    const [lastRequestStatus, setLastRequestStatus] = useState({});
    const [latestRequest, setLatestRequest] = useState(null);
    const seenReturnRequestsRef = useRef(new Set(JSON.parse(localStorage.getItem('seenReturnRequestNotifications') || '[]')));

    const userRole = localStorage.getItem('role') || 'worker';
    const userId = localStorage.getItem('id');

    useEffect(() => {
        if (userRole !== 'worker' || !userId) return;

        const pollRequests = async () => {
            try {
                console.log('Polling requests for worker_id:', userId);
                const response = await axios.get(`${ApiURL}/getWorkerRequests.php?worker_id=${userId}`, {
                    withCredentials: true,
                });
                console.log('Worker Requests Response:', response.data);
                const requests = Array.isArray(response.data) ? response.data : [];

                if (requests.length === 0) {
                    setLatestRequest(null);
                    if (typeof onLatestRequestUpdate === 'function') {
                        onLatestRequestUpdate(null);
                    } else {
                        console.warn('onLatestRequestUpdate is not a function');
                    }
                    return;
                }

                const latest = requests.reduce((latest, current) => {
                    const latestDate = latest.updated_at ? new Date(latest.updated_at) : new Date(0);
                    const currentDate = current.updated_at ? new Date(current.updated_at) : new Date(0);
                    return currentDate > latestDate ? current : latest;
                }, requests[0]);

                const dismissedIds = JSON.parse(localStorage.getItem('dismissedRequestIds') || '[]');
                if (!dismissedIds.includes(latest.id) && latest.status !== 'inprogress') {
                    setLatestRequest(latest);
                    if (typeof onLatestRequestUpdate === 'function') {
                        onLatestRequestUpdate(latest);
                    } else {
                        console.warn('onLatestRequestUpdate is not a function');
                    }
                } else {
                    setLatestRequest(null);
                    if (typeof onLatestRequestUpdate === 'function') {
                        onLatestRequestUpdate(null);
                    } else {
                        console.warn('onLatestRequestUpdate is not a function');
                    }
                }

                requests.forEach((request) => {
                    const requestId = request.id;
                    const prevStatus = lastRequestStatus[requestId]?.status;
                    const currentStatus = request.status;

                    if (prevStatus && prevStatus !== currentStatus && currentStatus !== 'pending' && !dismissedIds.includes(requestId)) {
                        const message = currentStatus === 'approved' ? 'Your request has been approved!' : 'Your request has been rejected.';
                        toast[currentStatus === 'approved' ? 'success' : 'error'](
                            <div>
                                <p>{message}</p>
                                {currentStatus === 'rejected' && (
                                    <button
                                        className="text-blue-500 underline text-xs mt-1"
                                        onClick={() => toast.info(`Rejection Reason: ${request.rejection_reason || 'No reason provided'}`)}
                                    >
                                        View More
                                    </button>
                                )}
                            </div>,
                            {
                                position: 'top-right',
                                autoClose: 3000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                            }
                        );
                    }
                });

                setLastRequestStatus(
                    requests.reduce((acc, request) => ({
                        ...acc,
                        [request.id]: { status: request.status, rejection_reason: request.rejection_reason },
                    }), {})
                );
            } catch (err) {
                console.error('Notification Error:', err.message);
            }
        };

        pollRequests();
        const interval = setInterval(pollRequests, 30000);

        return () => clearInterval(interval);
    }, [userRole, userId, onLatestRequestUpdate]);

    useEffect(() => {
        if (!['admin', 'company_owner'].includes(userRole) || !userId) return;

        const pollReturnRequests = async () => {
            try {
                const response = await axios.get(`${ApiURL}/getReturnRequests.php`, {
                    params: { user_id: userId, role: userRole },
                    withCredentials: true,
                });
                const requests = response.data?.status === 'success' && Array.isArray(response.data.requests)
                    ? response.data.requests
                    : [];

                const pendingWorkerReturns = requests.filter((request) =>
                    String(request.status || '').toLowerCase() === 'pending' &&
                    String(request.requester_role || '').toLowerCase() === 'worker' &&
                    request.approval_role === 'approver'
                );

                pendingWorkerReturns.forEach((request) => {
                    const requestId = String(request.id);
                    if (seenReturnRequestsRef.current.has(requestId)) return;

                    seenReturnRequestsRef.current.add(requestId);
                    localStorage.setItem(
                        'seenReturnRequestNotifications',
                        JSON.stringify(Array.from(seenReturnRequestsRef.current).slice(-100))
                    );

                    toast.info(
                        `${request.requester_name || request.worker_name || 'Worker'} sent a return request of ₹${Number(request.total_return_amount || 0).toLocaleString('en-IN')}`,
                        {
                            position: 'top-right',
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                        }
                    );
                });
            } catch (err) {
                console.error('Return notification error:', err.message);
            }
        };

        pollReturnRequests();
        const interval = setInterval(pollReturnRequests, 30000);
        return () => clearInterval(interval);
    }, [userRole, userId]);

    return null;
};

export default WorkerNotifications;
